import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { MatDialog } from '@angular/material/dialog';

import {
  catchError,
  delay,
  EMPTY,
  forkJoin,
  map,
  retryWhen,
  scan,
  switchMap,
  takeWhile,
  tap
} from 'rxjs';
import { ComponentStore } from '@ngrx/component-store';

import {
  PrListItem,
  PrListCustomFilters,
  PrSorting,
  PrListFilters
} from '../models';
import { PrListService } from '../services/pr-list.service';
import { tableFiltersOptions } from '@app/pages/procurement/pr/pr-container/pr-list/constants';
import { PurchasingProcedureModalComponent } from '@app/shared/components/modals/purchasing-procedure-modal/purchasing-procedure-modal.component';
import { DistributionService } from '@app/services/distribution.service';
import { TablePagination } from '@app/shared/models';
import { ChooseRfxModalComponent } from '@app/shared/components/modals/choose-rfx-modal/choose-rfx-modal.component';
import { ChooseRfxModalData } from '@app/shared/components/modals/choose-rfx-modal/models/choose-rfx-modal.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { showAlertMsg } from '@app/shared/utils';
import { ProcedureService } from '@app/services/rfx/procedure.service';

interface State {
  filters: PrListFilters;
  dynamicFiltersOptions: PrListCustomFilters;
  items: PrListItem[];
  pagination: TablePagination;
}

@Injectable()
export class PrListStore extends ComponentStore<State> {
  constructor(
    private prListService: PrListService,
    private dialog: MatDialog,
    private distributionService: DistributionService,
    private router: Router,
    private snackBar: MatSnackBar,
    private procedureService: ProcedureService
  ) {
    super({
      filters: {
        statuses: [],
        currencies: [],
        types: [],
        authors: [],
        dateRange: null,
        sortParameter: 'Number',
        sortType: 'DESC',
        searchValue: ''
      },
      dynamicFiltersOptions: {
        statuses: [],
        currencies: [],
        types: [],
        authors: [],
        dateRange: null
      },
      items: [],
      pagination: {
        currentPage: 1,
        pageSize: 10,
        totalItems: 0
      }
    });
  }

  // effects
  public readonly getPrListData = this.effect<boolean>(trigger$ =>
    trigger$.pipe(
      switchMap(updateDynamicFilter =>
        this.prListService
          .getPrList(
            this.get(state => ({ ...state.filters, ...state.pagination }))
          )
          .pipe(
            map(res => ({
              res: res.value,
              updateDynamicFilter
            })),
            catchError(() => EMPTY)
          )
      ),
      tap(({ res, updateDynamicFilter }) => {
        this.patchState({
          items: res.items,
          pagination: {
            currentPage: res.currentPage,
            pageSize: res.pageSize,
            totalItems: res.totalItems
          }
        });

        updateDynamicFilter &&
          this.patchState({
            dynamicFiltersOptions: {
              statuses: res.statuses,
              currencies: res.currencies,
              types: res.types,
              authors: res.authors,
              dateRange: res.dateRange
            }
          });
      })
    )
  );

  public removePrs = this.effect<string[]>(trigger$ =>
    trigger$.pipe(
      switchMap(prIds =>
        this.prListService.removePrs(prIds).pipe(
          tap(() => {
            this.getPrListData(true);
          }),
          catchError(() => EMPTY)
        )
      )
    )
  );

  public openCreateTenderAuctionModal = this.effect<PrListItem[]>(trigger$ =>
    trigger$.pipe(
      tap(prs => {
        const categories = prs
          .flatMap(p =>
            p.categories.map(c => ({
              name: c.categoryValue,
              id: c.categoryId
            }))
          )
          .filter(
            (obj, index, self) => index === self.findIndex(o => o.id === obj.id)
          );

        this.dialog.open(PurchasingProcedureModalComponent, {
          data: {
            moveNext: (id: string, inNewLot: boolean) =>
              this.moveNextAll({
                from: prs.map(pr => pr.id),
                to: id,
                inNewLot,
                handleError: true
              }),
            categories
          }
        });
      })
    )
  );

  public openChooseTenderAuctionModal = this.effect<string[]>(trigger$ =>
    trigger$.pipe(
      tap(procedureId =>
        this.dialog.open<ChooseRfxModalComponent, ChooseRfxModalData>(
          ChooseRfxModalComponent,
          {
            width: '1250px',
            data: {
              save: (toId, inNewLot) =>
                this.moveNextAll({
                  from: procedureId,
                  to: toId,
                  inNewLot,
                  handleError: false
                }),
              close: () => this.dialog.closeAll()
            }
          }
        )
      )
    )
  );

  private moveNextAll = this.effect<{
    from: string[];
    to: string;
    inNewLot: boolean;
    handleError: boolean;
  }>(trigger$ =>
    trigger$.pipe(
      delay(1000),
      switchMap(({ from, to, inNewLot, handleError }) =>
        forkJoin(
          from.map(fromId =>
            this.distributionService
              .moveNextAll({
                fromProcessId: fromId,
                toProcessId: to,
                moveItemsConfiguration: {
                  inNewLot
                }
              })
              .pipe(
                retryWhen(errors =>
                  errors.pipe(
                    scan((attempt, error) => {
                      if (attempt >= 2) {
                        if (handleError) {
                          this.handleMoveNextAllError(to);
                        }
                        this.dialog.closeAll();

                        showAlertMsg(
                          'ITEMS_ALREADY_USE_IN_SOME_TENDER',
                          this.snackBar,
                          {
                            duration: 5000
                          }
                        );

                        throw error;
                      }
                      return attempt + 1;
                    }, 0),
                    delay(1000),
                    takeWhile(attempt => attempt <= 2, true)
                  )
                )
              )
          )
        ).pipe(
          tap(() => {
            this.dialog.closeAll();

            this.router.navigate([
              `procurement/tender-auction/tender-journal/${to}`
            ]);
          }),
          catchError(() => EMPTY)
        )
      )
    )
  );

  private handleMoveNextAllError = this.effect<string>(trigger$ =>
    trigger$.pipe(
      switchMap(id =>
        this.procedureService.deleteProcedure(id).pipe(
          tap(() => {
            this.dialog.closeAll();

            showAlertMsg('ITEMS_ALREADY_USE_IN_SOME_TENDER', this.snackBar, {
              duration: 5000
            });
          }),
          catchError(() => EMPTY)
        )
      )
    )
  );

  // reducers
  public updatePages = this.updater<Partial<TablePagination>>(
    (state, filter) => ({
      ...state,
      pagination: {
        ...state.pagination,
        ...filter
      }
    })
  );

  public updateSorting = this.updater<PrSorting>((state, sorting) => ({
    ...state,
    filters: {
      ...state.filters,
      sortParameter: sorting.sortParameter || 'Number',
      sortType: sorting.sortType || 'DESC'
    }
  }));

  public updateSearchValue = this.updater<string>((state, searchValue) => ({
    ...state,
    filters: {
      ...state.filters,
      searchValue
    }
  }));

  public updateFilters = this.updater<PrListCustomFilters>(
    (state, filters) => ({
      ...state,
      filters: {
        ...state.filters,
        ...filters
      }
    })
  );

  // selectors
  public prListData$ = this.select(state => ({
    items: state.items,
    pagination: state.pagination
  }));

  public piListFilters$ = this.select(state => {
    return tableFiltersOptions.map(filter => {
      const dynamicFilterOptions = (state.dynamicFiltersOptions as any)[
        filter.def!
      ];
      const filterOptions = (state.filters as any)[filter.def!];

      return {
        ...filter,
        options:
          dynamicFilterOptions?.map((df: string) => ({
            name: df,
            value: df,
            checked: filterOptions.includes(df)
          })) || []
      };
    });
  });
}
